import React, { useEffect, useState, useContext, Suspense } from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import moment from "moment";

import LoggedInLayout from "../layout";
import TicketResponsiveContainer from "../pages/TicketResponsiveContainer";
import Login from "../pages/Login/";
import Signup from "../pages/Signup";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import Route from "./Route";

// Páginas carregadas sob demanda (code-splitting): só baixam quando a tela é
// aberta, deixando o carregamento inicial (login/tickets) muito mais leve.
const Dashboard = React.lazy(() => import("../pages/Dashboard/"));
const Connections = React.lazy(() => import("../pages/Connections/"));
const Settings = React.lazy(() => import("../pages/Settings/"));
const Financeiro = React.lazy(() => import("../pages/Financeiro/"));
const Users = React.lazy(() => import("../pages/Users"));
const Contacts = React.lazy(() => import("../pages/Contacts/"));
const ContactImportPage = React.lazy(() => import("../pages/Contacts/import"));
const ChatMoments = React.lazy(() => import("../pages/Moments"));
const Queues = React.lazy(() => import("../pages/Queues/"));
const Tags = React.lazy(() => import("../pages/Tags/"));
const MessagesAPI = React.lazy(() => import("../pages/MessagesAPI/"));
const Helps = React.lazy(() => import("../pages/Helps/"));
const ContactLists = React.lazy(() => import("../pages/ContactLists/"));
const ContactListItems = React.lazy(() => import("../pages/ContactListItems/"));
const Companies = React.lazy(() => import("../pages/Companies/"));
const Wallets = React.lazy(() => import("../pages/Wallets/"));
const QuickMessages = React.lazy(() => import("../pages/QuickMessages/"));
const Schedules = React.lazy(() => import("../pages/Schedules"));
const Campaigns = React.lazy(() => import("../pages/Campaigns"));
const CampaignsConfig = React.lazy(() => import("../pages/CampaignsConfig"));
const CampaignReport = React.lazy(() => import("../pages/CampaignReport"));
const Annoucements = React.lazy(() => import("../pages/Annoucements"));
const Chat = React.lazy(() => import("../pages/Chat"));
const CustomerAttendances = React.lazy(() => import("../pages/CustomerAttendances"));
const Prompts = React.lazy(() => import("../pages/Prompts"));
const AllConnections = React.lazy(() => import("../pages/AllConnections/"));
const Reports = React.lazy(() => import("../pages/Reports"));
const RelatorioVendas = React.lazy(() => import("../pages/RelatorioVendas"));
const Subscription = React.lazy(() => import("../pages/Subscription/"));
const QueueIntegration = React.lazy(() => import("../pages/QueueIntegration"));
const Files = React.lazy(() => import("../pages/Files/"));
const ToDoList = React.lazy(() => import("../pages/ToDoList/"));
const Kanban = React.lazy(() => import("../pages/Kanban"));
const TagsKanban = React.lazy(() => import("../pages/TagsKanban"));
const BirthdaySettingsPage = React.lazy(() => import("../pages/BirthdaySettings"));
const CallHistoricals = React.lazy(() => import("../pages/CallHistoricals"));
const FlowBuilderConfig = React.lazy(() =>
  import("../pages/FlowBuilderConfig").then(m => ({ default: m.FlowBuilderConfig }))
);
const FlowBuilder = React.lazy(() => import("../pages/FlowBuilder"));
const FlowDefault = React.lazy(() => import("../pages/FlowDefault"));
const CampaignsPhrase = React.lazy(() => import("../pages/CampaignsPhrase"));
const PostDelivery = React.lazy(() => import("../pages/PostDelivery"));

const RoutesFallback = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      width: "100%",
    }}
  >
    Carregando…
  </div>
);

const RoutesContent = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  // Verificar se a empresa está vencida
  const isCompanyExpired = () => {
    if (!user || !user.company || user.company.id === 1) {
      return false; // Empresa ID 1 nunca expira
    }

    const dueDate = user.company.dueDate;
    if (!dueDate) return false;

    // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
    const hojeInicio = moment().startOf('day');
    const vencimentoInicio = moment(dueDate).startOf('day');

    // Empresa está vencida apenas após o dia do vencimento
    return hojeInicio.isAfter(vencimentoInicio, 'day');
  };

  return (
    <TicketsContextProvider>
      <Switch>
        <Route exact path="/login" component={Login} />
        <Route exact path="/signup" component={Signup} />
        <WhatsAppsProvider>
          <LoggedInLayout hideMenu={isCompanyExpired()}>
            <Suspense fallback={<RoutesFallback />}>
                <Route
                  exact
                  path="/financeiro"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/financeiro-aberto"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/companies"
                  component={Companies}
                  isPrivate
                />
                <Route
                  exact
                  path="/birthday-settings"
                  component={BirthdaySettingsPage}
                  isPrivate
                />
                <Route exact path="/" component={Dashboard} isPrivate />
                <Route exact path="/call-historicals" component={CallHistoricals} isPrivate />
                <Route
                  exact
                  path="/tickets/:ticketId?"
                  component={TicketResponsiveContainer}
                  isPrivate
                />
                <Route
                  exact
                  path="/customer-attendances"
                  component={CustomerAttendances}
                  isPrivate
                />
                <Route
                  exact
                  path="/connections"
                  component={Connections}
                  isPrivate
                />
                <Route
                  exact
                  path="/quick-messages"
                  component={QuickMessages}
                  isPrivate
                />
                <Route exact path="/todolist" component={ToDoList} isPrivate />
                <Route
                  exact
                  path="/schedules"
                  component={Schedules}
                  isPrivate
                />
                <Route exact path="/tags" component={Tags} isPrivate />
                <Route exact path="/contacts" component={Contacts} isPrivate />
                <Route
                  exact
                  path="/contacts/import"
                  component={ContactImportPage}
                  isPrivate
                />
                <Route exact path="/wallets" component={Wallets} isPrivate />
                <Route exact path="/helps" component={Helps} isPrivate />
                <Route exact path="/users" component={Users} isPrivate />
                <Route
                  exact
                  path="/messages-api"
                  component={MessagesAPI}
                  isPrivate
                />
                <Route
                  exact
                  path="/settings"
                  component={Settings}
                  isPrivate
                />
                <Route exact path="/queues" component={Queues} isPrivate />
                <Route exact path="/reports" component={Reports} isPrivate />
                <Route
                  exact
                  path="/relatorio-vendas"
                  component={RelatorioVendas}
                  isPrivate
                />
                <Route
                  exact
                  path="/queue-integration"
                  component={QueueIntegration}
                  isPrivate
                />
                <Route
                  exact
                  path="/announcements"
                  component={Annoucements}
                  isPrivate
                />
                <Route exact path="/chats/:id?" component={Chat} isPrivate />
                <Route exact path="/files" component={Files} isPrivate />
                <Route
                  exact
                  path="/moments"
                  component={ChatMoments}
                  isPrivate
                />
                <Route exact path="/Kanban" component={Kanban} isPrivate />
                <Route
                  exact
                  path="/TagsKanban"
                  component={TagsKanban}
                  isPrivate
                />
                <Route exact path="/prompts" component={Prompts} isPrivate />
                <Route exact path="/post-delivery" component={PostDelivery} isPrivate />
                <Route
                  exact
                  path="/allConnections"
                  component={AllConnections}
                  isPrivate
                />

                <Route
                  exact
                  path="/phrase-lists"
                  component={CampaignsPhrase}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilders"
                  component={FlowBuilder}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilder/:id?"
                  component={FlowBuilderConfig}
                  isPrivate
                />

                {showCampaigns && (
                  <>
                    <Route
                      exact
                      path="/contact-lists"
                      component={ContactLists}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/contact-lists/:contactListId/contacts"
                      component={ContactListItems}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns"
                      component={Campaigns}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaign/:campaignId/report"
                      component={CampaignReport}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns-config"
                      component={CampaignsConfig}
                      isPrivate
                    />
                  </>
                )}
            </Suspense>
              </LoggedInLayout>
            </WhatsAppsProvider>
          </Switch>
          <ToastContainer position="top-center" autoClose={3000} />
        </TicketsContextProvider>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RoutesContent />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
